import { z } from 'zod';

/**
 * Call scripts (docs/36 §8.3, 0129): what the agent says, filled in from the contact being called.
 *
 * A script is STEPS. Each has text with {{variables}}, BUTTONS that move to another step (and can
 * pick the disposition), and FIELDS the agent fills in, which are written back to the contact and
 * are variables themselves from then on.
 *
 * The text is a small subset of markdown -- **bold**, "- " list lines, line breaks -- and it is
 * RENDERED AS TEXT: renderScript returns plain strings and flags, never markup. The values come from
 * a client's spreadsheet, so nothing a contact's record holds can put markup into the workspace.
 */

const Key = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, 'Lowercase letters, digits and _');
const DispositionCode = z.string().regex(/^[a-z][a-z0-9_]{1,30}$/);

export const SCRIPT_FIELD_TYPES = ['text', 'number', 'date', 'choice', 'yes_no'] as const;
export type ScriptFieldType = (typeof SCRIPT_FIELD_TYPES)[number];

export const ScriptFieldSchema = z.object({
  key: Key,
  label: z.string().trim().min(1).max(80),
  type: z.enum(SCRIPT_FIELD_TYPES),
  /** The choices, for type choice. */
  options: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
  /** Must be filled in before the call is saved with one of these dispositions. */
  required_for: z.array(DispositionCode).max(30).default([]),
}).strict();
export type ScriptField = z.infer<typeof ScriptFieldSchema>;

export const ScriptButtonSchema = z.object({
  label: z.string().trim().min(1).max(40),
  /** The step it moves to; null stays on this one. */
  go_to: Key.nullable().default(null),
  /** Chooses this disposition in the call's outcome. */
  disposition: DispositionCode.nullable().default(null),
}).strict();

export const ScriptStepSchema = z.object({
  key: Key,
  title: z.string().trim().min(1).max(60),
  text: z.string().max(4000).default(''),
  buttons: z.array(ScriptButtonSchema).max(8).default([]),
  fields: z.array(ScriptFieldSchema).max(20).default([]),
}).strict();
export type ScriptStep = z.infer<typeof ScriptStepSchema>;

export const ScriptBodySchema = z.object({
  steps: z.array(ScriptStepSchema).min(1).max(30),
}).strict().superRefine((b, issue) => {
  const steps = new Set<string>();
  for (const [i, s] of b.steps.entries()) {
    if (steps.has(s.key)) issue.addIssue({ code: z.ZodIssueCode.custom, path: ['steps', i, 'key'], message: `Two steps are called ${s.key}.` });
    steps.add(s.key);
  }
  const fields = new Set<string>();
  for (const [i, s] of b.steps.entries()) {
    for (const [j, btn] of s.buttons.entries()) {
      if (btn.go_to && !steps.has(btn.go_to)) {
        issue.addIssue({ code: z.ZodIssueCode.custom, path: ['steps', i, 'buttons', j, 'go_to'], message: `No step called ${btn.go_to}.` });
      }
    }
    for (const [j, f] of s.fields.entries()) {
      if (fields.has(f.key)) issue.addIssue({ code: z.ZodIssueCode.custom, path: ['steps', i, 'fields', j, 'key'], message: `Two fields are called ${f.key}.` });
      fields.add(f.key);
      if (f.type === 'choice' && f.options.length < 2) {
        issue.addIssue({ code: z.ZodIssueCode.custom, path: ['steps', i, 'fields', j, 'options'], message: 'A choice needs at least two options.' });
      }
    }
  }
});
export type ScriptBody = z.infer<typeof ScriptBodySchema>;

export const ScriptUpsertSchema = z.object({
  name: z.string().trim().min(1).max(120),
  body: ScriptBodySchema,
}).strict();
export type ScriptUpsert = z.infer<typeof ScriptUpsertSchema>;

export interface ScriptSummary {
  id: string; name: string; version: number; steps: number; campaigns: number; updated_at: string;
}

export interface ScriptDetail extends ScriptSummary {
  body: ScriptBody;
  versions: Array<{ version: number; created_at: string; by: string | null }>;
  /** The variables the account's lists provide, for the editor. */
  available: Array<{ name: string; label: string }>;
  /** Variables and dispositions the script uses that nothing provides. */
  problems: string[];
}

/** The script as a call shows it: the version the call recorded. */
export interface CallScript {
  id: string; name: string; version: number; body: ScriptBody;
}

// ============================================================================ variables

/** Variables every call has, whatever the list. */
export const BUILTIN_VARIABLES: ReadonlyArray<readonly [string, string]> = [
  ['first_name', 'First name'], ['last_name', 'Last name'], ['email', 'Email'], ['phone', 'Number being called'],
  ['agent.first_name', 'Agent’s first name'], ['agent.name', 'Agent’s name'],
  ['campaign.name', 'Campaign'], ['caller_id', 'Number shown to them'], ['today', 'Today’s date'],
];
const BUILTIN = new Set(BUILTIN_VARIABLES.map(([n]) => n));

// {{ name }} or {{ name | default }}. Names are dotted keys; anything else is left as written.
const VAR = /\{\{\s*([a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)?)\s*(?:\|([^}]{0,80}))?\}\}/g;

/** Every variable a script's text uses, once each. */
export function scriptVariables(body: ScriptBody): string[] {
  const out = new Set<string>();
  for (const s of body.steps) for (const m of s.text.matchAll(VAR)) out.add(m[1]!);
  return [...out];
}

/**
 * What a script uses that nothing provides: a variable that is neither built in, nor a field of the
 * lists (`custom.<key>`), nor captured by the script itself; and a disposition the account does not
 * have. Empty is a script that can go live.
 */
export function checkScript(body: ScriptBody, listFieldKeys: readonly string[], dispositionCodes: readonly string[]): string[] {
  const custom = new Set([...listFieldKeys, ...body.steps.flatMap((s) => s.fields.map((f) => f.key))]);
  const codes = new Set(dispositionCodes);
  const out: string[] = [];
  for (const v of scriptVariables(body)) {
    if (BUILTIN.has(v)) continue;
    if (v.startsWith('custom.') && custom.has(v.slice(7))) continue;
    out.push(v.startsWith('custom.') ? `{{${v}}}: no list has a field called ${v.slice(7)}` : `{{${v}}}: not a variable`);
  }
  for (const s of body.steps) {
    for (const b of s.buttons) {
      if (b.disposition && !codes.has(b.disposition)) out.push(`${s.title}: the button "${b.label}" picks ${b.disposition}, which is not a disposition`);
    }
    for (const f of s.fields) {
      for (const d of f.required_for) if (!codes.has(d)) out.push(`${f.label}: required for ${d}, which is not a disposition`);
    }
  }
  return [...new Set(out)];
}

// ============================================================================ rendering

export type ScriptSpan =
  | { kind: 'text'; text: string; bold: boolean }
  /** A variable with no value and no default: shown to the agent as missing, never as a blank. */
  | { kind: 'missing'; name: string; bold: boolean };
export interface ScriptBlock { type: 'p' | 'li'; spans: ScriptSpan[] }

/**
 * A step's text with its variables filled in, as plain strings.
 *
 * Variables are filled BEFORE the markdown is read, per span, so a value containing ** or a leading
 * "- " is only ever text: the formatting comes from the script, never from the record.
 */
export function renderScript(text: string, vars: Readonly<Record<string, string | null | undefined>>): ScriptBlock[] {
  const blocks: ScriptBlock[] = [];
  let para: ScriptSpan[] | null = null;
  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trimEnd();
    if (!line.trim()) { para = null; continue; }
    const li = /^\s*[-*]\s+(.*)$/.exec(line);
    const spans = renderLine(li ? li[1]! : line, vars);
    if (li) { blocks.push({ type: 'li', spans }); para = null; continue; }
    if (para) { para.push({ kind: 'text', text: '\n', bold: false }, ...spans); continue; }
    para = spans;
    blocks.push({ type: 'p', spans: para });
  }
  return blocks;
}

function renderLine(line: string, vars: Readonly<Record<string, string | null | undefined>>): ScriptSpan[] {
  const out: ScriptSpan[] = [];
  // ** toggles bold; the markers themselves come only from the script's own text.
  line.split('**').forEach((part, i) => {
    const bold = i % 2 === 1;
    let at = 0;
    for (const m of part.matchAll(VAR)) {
      if (m.index! > at) out.push({ kind: 'text', text: part.slice(at, m.index), bold });
      const value = vars[m[1]!];
      const fallback = m[2]?.trim();
      if (value !== null && value !== undefined && String(value).trim() !== '') out.push({ kind: 'text', text: String(value), bold });
      else if (fallback) out.push({ kind: 'text', text: fallback, bold });
      else out.push({ kind: 'missing', name: m[1]!, bold });
      at = m.index! + m[0].length;
    }
    if (at < part.length) out.push({ kind: 'text', text: part.slice(at), bold });
  });
  return out;
}

// ============================================================================ captures

export type CaptureValue = string | number | boolean | null;

/**
 * The agent's captured values, checked against the script's fields: only its own keys, each of its
 * type. Returns the clean values, or the first problem in words.
 */
export function checkCaptures(
  body: ScriptBody, captures: Readonly<Record<string, unknown>>, disposition: string | null,
): { ok: true; values: Record<string, CaptureValue> } | { ok: false; why: string } {
  const fields = new Map(body.steps.flatMap((s) => s.fields).map((f) => [f.key, f]));
  const values: Record<string, CaptureValue> = {};
  for (const [k, v] of Object.entries(captures)) {
    const f = fields.get(k);
    if (!f) return { ok: false, why: `The script has no field ${k}.` };
    if (v === null || v === '') { values[k] = null; continue; }
    switch (f.type) {
      case 'text':
        if (typeof v !== 'string' || v.length > 500) return { ok: false, why: `${f.label}: text up to 500 characters.` };
        values[k] = v.trim(); break;
      case 'number': {
        const n = typeof v === 'number' ? v : typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v.trim()) ? Number(v) : NaN;
        if (!Number.isFinite(n)) return { ok: false, why: `${f.label}: a number.` };
        values[k] = n; break;
      }
      case 'date':
        if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v) || Number.isNaN(Date.parse(`${v}T00:00:00Z`))) {
          return { ok: false, why: `${f.label}: a date.` };
        }
        values[k] = v; break;
      case 'choice':
        if (typeof v !== 'string' || !f.options.includes(v)) return { ok: false, why: `${f.label}: one of the choices.` };
        values[k] = v; break;
      case 'yes_no':
        if (typeof v !== 'boolean') return { ok: false, why: `${f.label}: yes or no.` };
        values[k] = v; break;
    }
  }
  if (disposition) {
    for (const f of fields.values()) {
      if (f.required_for.includes(disposition) && (values[f.key] === undefined || values[f.key] === null)) {
        return { ok: false, why: `Fill in ${f.label} before saving it as this disposition.` };
      }
    }
  }
  return { ok: true, values };
}
